import React, { useEffect, useState } from "react";
import API from "../api/api";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { Modal, Button, Form, Badge } from "react-bootstrap";
import MultiSelectPermissions from "../components/MultiSelectPermissions";
import AppShell from "../components/ui/AppShell";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [currentRole, setCurrentRole] = useState({});
  const [formData, setFormData] = useState({
    role_name: "",
    description: "",
    permissions: [],
  });

  const fetchRoles = async () => {
    try {
      const response = await API.get("/roles");
      const rolesWithPermissions = response.data.map((role) => ({
        ...role,
        permissions: role.permissions || [],
      }));
      setRoles(rolesWithPermissions);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch roles");
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await API.get("/permissions");
      setPermissions(response.data || []);
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePermissionsChange = (selected) => {
    setFormData({ ...formData, permissions: selected });
  };

  const handleAdd = async () => {
    try {
      await API.post("/roles", formData);
      fetchRoles();
      setShowAdd(false);
      setFormData({ role_name: "", description: "", permissions: [] });
    } catch (err) {
      alert("Failed to add role");
    }
  };

  const handleEdit = async () => {
    try {
      await API.put(`/roles/${currentRole.id}`, formData);
      fetchRoles();
      setShowEdit(false);
    } catch (err) {
      alert("Failed to update role");
    }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/roles/${currentRole.id}`);
      fetchRoles();
      setShowDelete(false);
    } catch (err) {
      alert("Failed to delete role");
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Roles"
        description="Define roles and attach permissions."
        actions={
          <Button variant="primary" onClick={() => setShowAdd(true)}>
            <FaPlus /> Add Role
          </Button>
        }
      />

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="vp-loading">Loading roles…</div>
      ) : roles.length === 0 ? (
        <EmptyState
          title="No roles yet"
          description="Create a role to control what agents can access."
          action={
            <Button variant="primary" onClick={() => setShowAdd(true)}>
              <FaPlus /> Add Role
            </Button>
          }
        />
      ) : (
        <div className="vp-table-wrap table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>ID</th>
                <th>Role Name</th>
                <th>Description</th>
                <th>Permissions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td>{role.id}</td>
                  <td>{role.role_name}</td>
                  <td>{role.description}</td>
                  <td>
                    {(role.permissions || []).map((p) => (
                      <Badge bg="info" className="me-1" key={p.id}>
                        {p.permission_name}
                      </Badge>
                    ))}
                  </td>
                  <td>
                    <div className="vp-table-actions">
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => {
                          setCurrentRole(role);
                          setFormData({
                            role_name: role.role_name,
                            description: role.description,
                            permissions: (role.permissions || []).map((p) => p.id),
                          });
                          setShowEdit(true);
                        }}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          setCurrentRole(role);
                          setShowDelete(true);
                        }}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showAdd} onHide={() => setShowAdd(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Role Name</Form.Label>
              <Form.Control
                type="text"
                name="role_name"
                value={formData.role_name}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Permissions</Form.Label>
              <MultiSelectPermissions
                permissions={permissions}
                selectedPermissions={formData.permissions}
                onChange={handlePermissionsChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEdit} onHide={() => setShowEdit(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Role Name</Form.Label>
              <Form.Control
                type="text"
                name="role_name"
                value={formData.role_name}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Permissions</Form.Label>
              <MultiSelectPermissions
                permissions={permissions}
                selectedPermissions={formData.permissions}
                onChange={handlePermissionsChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEdit}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDelete} onHide={() => setShowDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{currentRole.role_name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </AppShell>
  );
};

export default Roles;
